import type { OnboardingSessionData } from "./onboardingSession";

const OTHER_RECIPIENT = "Other";
const OTHER_OCCASION = "Something else...";
const PROMPT_SEPARATOR = " · ";
const LATIN_GENRE = "Latin";
const LATIN_GENRE_LANGUAGE = "Spanish";

function resolveOnboardingSongLanguage(
  data: OnboardingSessionData,
): string | null {
  if (data.selectedGenres.includes(LATIN_GENRE)) {
    return LATIN_GENRE_LANGUAGE;
  }
  return null;
}

/** When Latin is in guided genre selections, ensure the generation prompt requests Spanish vocals. */
export function applyLatinGenreLanguageToPrompt(
  prompt: string,
  guidedSelections?: Record<string, string[]>,
): string {
  const genres = guidedSelections?.genre ?? [];
  if (!genres.includes(LATIN_GENRE)) {
    return prompt;
  }
  if (/\bspanish\b/i.test(prompt) || /\blanguage:\s*spanish/i.test(prompt)) {
    return prompt;
  }
  const trimmed = prompt.trim();
  if (!trimmed) {
    return `Language: ${LATIN_GENRE_LANGUAGE}`;
  }
  return `${trimmed}${PROMPT_SEPARATOR}Language: ${LATIN_GENRE_LANGUAGE}`;
}

const FEMININE_RECIPIENTS = new Set([
  "Wife",
  "Girlfriend",
  "Mother",
  "Daughter",
  "Granddaughter",
]);

const MASCULINE_RECIPIENTS = new Set([
  "Husband",
  "Boyfriend",
  "Father",
  "Son",
  "Grandson",
]);

export function resolveRecipientValue(data: OnboardingSessionData): string | null {
  if (!data.selectedRecipient) return null;
  return data.selectedRecipient === OTHER_RECIPIENT
    ? data.otherRecipientText.trim()
    : data.selectedRecipient;
}

/** Dashboard / querydata display name — e.g. `Song for Rosy.mp3`. */
export function buildSongForDisplayFilename(
  recipientName: string | null | undefined,
  sourceFilename?: string | null,
): string {
  const ext =
    sourceFilename?.includes(".") ?
      (sourceFilename.split(".").pop()?.toLowerCase() ?? "mp3")
    : "mp3";
  const name = recipientName?.trim();
  if (!name) {
    const trimmed = sourceFilename?.trim();
    return trimmed && trimmed.length > 0 ? trimmed : `Your Custom Song.${ext}`;
  }
  return `Song for ${name}.${ext}`;
}

export function resolveRecipientDisplayNameFromCustomData(
  customData?: Record<string, unknown> | null,
): string {
  const name =
    typeof customData?.name === "string" ? customData.name.trim() : "";
  if (name) return name;
  const songfor =
    typeof customData?.songfor === "string" ? customData.songfor.trim() : "";
  return songfor;
}

export function resolveOccasionValue(data: OnboardingSessionData): string | null {
  if (!data.selectedOccasion) return null;
  return data.selectedOccasion === OTHER_OCCASION
    ? data.otherOccasionText.trim()
    : data.selectedOccasion;
}

function getNameLabel(recipient: string | null): string {
  if (!recipient) return "Name";
  if (FEMININE_RECIPIENTS.has(recipient)) return "Her name";
  if (MASCULINE_RECIPIENTS.has(recipient)) return "His name";
  return "Their name";
}

function buildMusicStylesValue(data: OnboardingSessionData): string | null {
  const parts = [
    ...data.selectedGenres,
    ...data.selectedStyles,
    ...(data.selectedTempo ? [data.selectedTempo] : []),
    ...data.selectedInstruments,
  ];
  return parts.length > 0 ? parts.join(", ") : null;
}

export function buildOnboardingSelections(
  data: OnboardingSessionData,
): Record<string, string[]> {
  const selections: Record<string, string[]> = {};
  const recipientValue = resolveRecipientValue(data);
  const name = data.recipientName.trim();
  const occasionValue = resolveOccasionValue(data);
  const specialDescription = data.recipientSpecialDescription.trim();
  const memoriesDescription = data.favoriteMemoriesDescription.trim();
  const heartMessage = data.heartMessageDescription.trim();
  const musicStylesValue = buildMusicStylesValue(data);

  if (recipientValue) selections.songfor = [recipientValue];
  if (name) selections.name = [name];
  if (occasionValue) selections.occasion = [occasionValue];
  if (musicStylesValue) selections.musicStyles = [musicStylesValue];
  if (specialDescription) selections.qualities = [specialDescription];
  if (memoriesDescription) selections.favoriteMemories = [memoriesDescription];
  if (heartMessage) selections.heartMessage = [heartMessage];
  if (data.selectedGenres.length > 0) selections.genre = [...data.selectedGenres];
  if (data.selectedStyles.length > 0) selections.style = [...data.selectedStyles];
  if (data.selectedTempo) selections.tempo = [data.selectedTempo];
  if (data.selectedInstruments.length > 0) {
    selections.instruments = [...data.selectedInstruments];
  }
  const songLanguage = resolveOnboardingSongLanguage(data);
  if (songLanguage) {
    selections.language = [songLanguage];
  }

  return selections;
}

export function buildOnboardingPrompt(data: OnboardingSessionData): string {
  const parts: string[] = [];
  const recipientValue = resolveRecipientValue(data);
  const name = data.recipientName.trim();
  const occasionValue = resolveOccasionValue(data);
  const specialDescription = data.recipientSpecialDescription.trim();
  const memoriesDescription = data.favoriteMemoriesDescription.trim();
  const heartMessage = data.heartMessageDescription.trim();

  if (recipientValue) parts.push(`Song for: ${recipientValue}`);
  if (name) {
    parts.push(`${getNameLabel(data.selectedRecipient)}: ${name}`);
  }
  if (occasionValue) parts.push(`Occasion: ${occasionValue}`);
  if (data.selectedGenres.length > 0) {
    parts.push(`Genre: ${data.selectedGenres.join(", ")}`);
  }
  if (data.selectedStyles.length > 0) {
    parts.push(`Style: ${data.selectedStyles.join(", ")}`);
  }
  if (data.selectedTempo) parts.push(`Tempo: ${data.selectedTempo}`);
  if (data.selectedInstruments.length > 0) {
    parts.push(`Instruments: ${data.selectedInstruments.join(", ")}`);
  }
  if (specialDescription) parts.push(`Qualities: ${specialDescription}`);
  if (memoriesDescription) parts.push(`Memories: ${memoriesDescription}`);
  if (heartMessage) parts.push(`Message: ${heartMessage}`);
  const songLanguage = resolveOnboardingSongLanguage(data);
  if (songLanguage) {
    parts.push(`Language: ${songLanguage}`);
  }

  if (parts.length > 0) {
    return parts.join(PROMPT_SEPARATOR);
  }

  return "personalized song";
}

export function truncateToWords(text: string, wordCount: number): string {
  const trimmed = text.trim();
  if (!trimmed) return "—";

  const words = trimmed.split(/\s+/).filter(Boolean);

  if (words.length === 1 && trimmed.length > 6) {
    return `${trimmed.slice(0, 6)}......`;
  }

  if (words.length <= wordCount) return trimmed;
  return `${words.slice(0, wordCount).join(" ")}....`;
}
